import { CalendarClock, Coins, User, Users2 } from 'lucide-react';
import usePublicEconomics from '@/hooks/usePublicEconomics';

/**
 * “Built For You” band — strategically positioned between the mandatory copy
 * blocks (CopySection) and the live matrix grid (RotatorPoolVisual) so it
 * lands right when a visitor is most primed to ask "is this for me?".
 *
 * Carries two strategic messages requested by the platform owner (relaunch copy, 29 Sep 2026):
 *   1. Year-long Sub Pack momentum on Day 1, Day 365 and beyond.
 *   2. Dual-audience framing — solo earners (no referrals required)
 *      AND team builders (sponsor match per direct-referral cycle).
 * Position cost, cycle payout and sponsor match are the live admin values.
 */
const BuiltForYouSection = () => {
  const { positionCost, cyclePayout, sponsorMatch } = usePublicEconomics();
  return (
    <section id="built-for-you" className="relative max-w-7xl mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-12 md:pb-20" data-testid="built-for-you-section">
      {/* Longevity headline band */}
      <div className="sc-glass-strong rounded-3xl p-6 md:p-10 relative overflow-hidden" data-testid="longevity-band">
        <div
          className="absolute -top-24 -left-16 w-[360px] h-[360px] pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
        />
        <div
          className="absolute -bottom-24 -right-10 w-[320px] h-[320px] pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,194,14,0.14), transparent 60%)',
          }}
        />
        <div className="relative z-10 max-w-4xl">
          <div className="sc-pill">
            <span className="sc-dot" />
            Built to Stay Attractive
          </div>
          <h2 className="font-display mt-4 text-[28px] md:text-[44px] leading-[1.06] font-bold tracking-tight">
            High-energy momentum on <span className="sc-gold-text">Day&nbsp;1</span>, <span className="sc-gold-text">Day&nbsp;365</span>, and far beyond.
          </h2>
          <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-[var(--sc-text-dim)] max-w-3xl">
            Traditional cyclers die when members stop buying and late joiners get buried at the bottom. 1SimpleCycler 3.0 solves this forever. Because members
            activate 52-week Sub Packs, existing positions continue dripping into the engine every single day for an entire year. Our active sub packs will not
            drop off quickly like other programs. Because every sub pack runs for a full year, our pool of active sub packs will only continue to compound and
            grow bigger as new people sign up daily, weekly, and monthly! Every new member who joins adds another continuous year-long stream of fillers to the
            collective pool. The engine accelerates as it grows, powered by alternating Random and FIFO cycles that keep everyone moving forward.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#FFC20E]">
            <CalendarClock className="w-3.5 h-3.5" />
            The engine accelerates as it grows.
          </div>
        </div>
      </div>

      {/* Two-audience cards */}
      <div className="mt-6 md:mt-8">
        <div className="max-w-3xl">
          <div className="sc-pill">
            <span className="sc-dot" />
            Two Kinds of Members. Both Win.
          </div>
          <h3 className="font-display mt-4 text-[24px] md:text-[34px] leading-[1.08] font-bold tracking-tight">
            You don't have to recruit. <span className="sc-gold-text">But if you do, you get rewarded.</span>
          </h3>
          <p className="mt-3 text-[14px] md:text-[16px] leading-relaxed text-[var(--sc-text-dim)]">
            Whether you're a quiet solo earner or a born team-builder, the platform pays you. Pick the lane that fits how you want to participate — or run both.
          </p>

          {/* Cost-of-entry transparency strip — factual mechanic, no return language. */}
          <div
            className="mt-5 inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-[12px] md:text-[13px] leading-snug"
            style={{
              background: 'rgba(255, 194, 14, 0.06)',
              borderColor: 'rgba(255, 194, 14, 0.40)',
            }}
            data-testid="position-cost-strip"
          >
            <Coins className="w-3.5 h-3.5 text-[#FFC20E] shrink-0" />
            <span className="text-[var(--sc-text-dim)]">
              Positions are just <strong className="text-[#FFE08A]">{positionCost} each</strong>. Just 3 micro-fillers complete a 3&times;1 cycle.
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7 md:mt-9">
          {/* Card 1 — Solo earners */}
          <div className="sc-glass rounded-2xl p-6 md:p-8 relative overflow-hidden" data-testid="audience-card-solo">
            <div
              className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(255,182,18,0.16), transparent 60%)',
              }}
            />
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center border"
              style={{
                background: 'rgba(255, 182, 18, 0.06)',
                borderColor: 'rgba(255, 182, 18, 0.32)',
              }}
            >
              <User className="w-5 h-5" style={{ color: '#FFC20E' }} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-5">For Solo Earners</div>
            <h4 className="font-display font-bold text-xl md:text-2xl mt-2 leading-snug">No recruiting required to earn.</h4>
            <p className="mt-3 text-[14px] md:text-[15px] leading-[1.7] text-[var(--sc-text-dim)]">
              You can earn and cycle <strong className="text-[#FFE08A]">without sponsoring a single person</strong>. The engine's alternating Random and FIFO
              heartbeats ensure solo positions cycle and pay straight to your balance.
            </p>
            <div className="sc-divider my-5" />
            <ul className="space-y-2 text-[13px] text-[var(--sc-text-dim)]">
              <li className="flex items-start gap-2">
                <Coins className="w-3.5 h-3.5 mt-1 text-[#FFC20E] shrink-0" />
                <span>
                  <strong className="text-[#FFE08A]">{cyclePayout} paid directly to you</strong> every time one of your {positionCost} positions cycles.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="sc-dot mt-2" /> Zero referrals required to participate or cycle.
              </li>
              <li className="flex items-start gap-2">
                <span className="sc-dot mt-2" /> 1-Year Sub Packs drip positions for you automatically 24/7.
              </li>
              <li className="flex items-start gap-2">
                <span className="sc-dot mt-2" /> True passive participation.
              </li>
            </ul>
          </div>

          {/* Card 2 — Team builders */}
          <div className="sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden" data-testid="audience-card-leaders">
            <div
              className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(255,194,14,0.28), transparent 60%)',
              }}
            />
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center border"
              style={{
                background: 'rgba(255, 182, 18, 0.12)',
                borderColor: 'rgba(255, 182, 18, 0.55)',
              }}
            >
              <Users2 className="w-5 h-5" style={{ color: '#FFC20E' }} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC20E] mt-5">For Team Builders</div>
            <h4 className="font-display font-bold text-xl md:text-2xl mt-2 leading-snug">
              Build a team. <span className="sc-gold-text">Accelerate your earnings.</span>
            </h4>
            <p className="mt-3 text-[14px] md:text-[15px] leading-[1.7] text-[var(--sc-text-dim)]">
              On top of your own <strong className="text-[#FFE08A]">{cyclePayout} cycle payouts</strong>, active team builders collect recurring sponsor
              matching bonuses whenever personal referrals cycle.
            </p>
            <div className="sc-divider my-5" />
            <ul className="space-y-2 text-[13px] text-[var(--sc-text-dim)]">
              <li className="flex items-start gap-2">
                <Coins className="w-3.5 h-3.5 mt-1 text-[#FFC20E] shrink-0" />
                <span>
                  <strong className="text-[#FFE08A]">{sponsorMatch}</strong> instant sponsor matching bonus every single time a direct referral's position
                  cycles.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="sc-dot mt-2" /> Continuous passive matching income across both Random and FIFO heartbeats.
              </li>
              <li className="flex items-start gap-2">
                <span className="sc-dot mt-2" /> Unlimited direct referrals — unlimited continuous matches.
              </li>
              <li className="flex items-start gap-2">
                <span className="sc-dot mt-2" /> Clean marketing tools and real-time tracking dashboard.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuiltForYouSection;
